import { useEffect, useState } from 'react'
import { Users, Info, UserPlus, User, UsersRound } from 'lucide-react'
import { SkeletonPage } from '@/components/ui/Skeleton'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts'
import { useAuth } from '@/contexts/AuthContext'

const COLORS = ['#94a3b8', '#0070ab', '#00c7ca', '#ed00be', '#f59e0b']

interface Distribution {
  pairs: number
  triples: number
  quads: number
  large: number
}

interface AttractionGroup {
  attraction_id: number
  attraction_name: string
  attraction_type: string
  total_bands: number
  inferred_groups: number
  solo_visitors: number
  avg_group_size: number | null
  median_group_size: number | null
  max_group_size: number
  distribution: Distribution
  implied_unique_parties: number
  band_to_party_ratio: number | null
  avg_intra_group_gap_sec: number | null
}

interface Summary {
  total_bands: number
  total_inferred_groups: number
  total_solo_visitors: number
  implied_unique_parties: number
  overall_avg_group_size: number | null
  overall_band_to_party: number | null
  pct_solo: number | null
  pct_pairs: number | null
  pct_families: number | null
  pct_large_groups: number | null
  ride_avg_group_size: number | null
  game_avg_group_size: number | null
  food_avg_group_size: number | null
}

interface GuestGroupData {
  summary: Summary | null
  attractions: AttractionGroup[]
}

function fmtPct(v: number | null): string {
  return v != null ? `${v.toFixed(1)}%` : '—'
}

function fmtNum(v: number): string {
  return v.toLocaleString()
}

function KpiCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: React.ReactNode }) {
  return (
    <div className="mm-card-v3 p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-ink-soft">{label}</span>
      </div>
      <div className="font-display font-bold text-xl text-ink-dark">{value}</div>
      {sub && <div className="text-xs text-ink-soft mt-1">{sub}</div>}
    </div>
  )
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: typeof Users; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4 text-butler-600" />
      <h2 className="font-display font-semibold text-base text-ink-dark">{title}</h2>
      {subtitle && <span className="text-xs text-ink-soft">— {subtitle}</span>}
    </div>
  )
}

export function GuestGroupsPage() {
  const { currentEventId } = useAuth()
  const [data, setData] = useState<GuestGroupData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (currentEventId == null) { setData(null); return }
    setLoading(true)
    const controller = new AbortController()
    fetch(`/api/dashboard/${currentEventId}/guest-groups`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false) })
      .catch(() => setLoading(false))
    return () => controller.abort()
  }, [currentEventId])

  if (currentEventId == null) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="rounded-lg border border-dashed border-slate-300 p-12 text-center">
          <h2 className="font-display font-bold text-lg text-ink-dark mb-2">Guest Groups</h2>
          <p className="text-sm text-ink-soft">Select an event to see group size inference.</p>
        </div>
      </div>
    )
  }

  if (loading && !data) {
    return <SkeletonPage />
  }

  if (!data?.summary) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mm-card-v3 p-8 text-center">
          <Info className="h-8 w-8 text-ink-soft mx-auto mb-3" />
          <h2 className="font-display font-semibold text-ink-dark mb-1">No Group Data Yet</h2>
          <p className="text-sm text-ink-soft">Guest group inference runs during the daily ETL archive cycle.</p>
        </div>
      </div>
    )
  }

  const s = data.summary

  const pieData = [
    { name: 'Solo', value: s.pct_solo ?? 0, color: COLORS[0] },
    { name: 'Pairs', value: s.pct_pairs ?? 0, color: COLORS[1] },
    { name: 'Families (3-4)', value: s.pct_families ?? 0, color: COLORS[2] },
    { name: 'Large (5+)', value: s.pct_large_groups ?? 0, color: COLORS[3] },
  ].filter(d => d.value > 0)

  const typeData = [
    { name: 'Rides', avg: s.ride_avg_group_size },
    { name: 'Games', avg: s.game_avg_group_size },
    { name: 'Food', avg: s.food_avg_group_size },
  ].filter(d => d.avg != null)

  const topAttractions = data.attractions.slice(0, 15)

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="font-display font-bold text-xl text-ink-dark">Guest Groups</h1>
        <p className="text-sm text-ink-soft mt-1">
          Inferred from consecutive band scans within 5 seconds at the same attraction.
          Bands scanned together = likely same party.
        </p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Total Bands"
          value={fmtNum(s.total_bands)}
          icon={<User className="h-3.5 w-3.5 text-ink-soft" />}
        />
        <KpiCard
          label="Implied Parties"
          value={fmtNum(s.implied_unique_parties)}
          sub={`Band-to-party ratio: ${s.overall_band_to_party?.toFixed(2) ?? '—'}`}
          icon={<UsersRound className="h-3.5 w-3.5 text-butler-600" />}
        />
        <KpiCard
          label="Groups Detected"
          value={fmtNum(s.total_inferred_groups)}
          sub={`Avg size: ${s.overall_avg_group_size?.toFixed(1) ?? '—'}`}
          icon={<UserPlus className="h-3.5 w-3.5 text-accent-500" />}
        />
        <KpiCard
          label="Solo Visitors"
          value={fmtNum(s.total_solo_visitors)}
          sub={fmtPct(s.pct_solo) + ' of all scans'}
          icon={<User className="h-3.5 w-3.5 text-ink-soft" />}
        />
      </div>

      {/* Charts row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Group composition pie */}
        <div className="mm-card-v3 p-4">
          <SectionHeader icon={Users} title="Group Composition" />
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name} ${value.toFixed(0)}%`}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`${value.toFixed(1)}%`, 'Percentage']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Avg group size by type */}
        {typeData.length > 0 && (
          <div className="mm-card-v3 p-4">
            <SectionHeader icon={UsersRound} title="Avg Group Size by Type" />
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={typeData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-hairline, #e2e8f0)" />
                  <XAxis type="number" domain={[0, 'auto']} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={60} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v: number) => [v.toFixed(1), 'Avg Group Size']} />
                  <Bar dataKey="avg" fill="#0070ab" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Per-attraction table */}
      <div className="mm-card-v3 p-4">
        <SectionHeader icon={Users} title="By Attraction" subtitle={`Top ${topAttractions.length} by volume`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-soft border-b border-slate-200">
                <th className="py-2 pr-4">Attraction</th>
                <th className="py-2 pr-3 text-right">Bands</th>
                <th className="py-2 pr-3 text-right">Groups</th>
                <th className="py-2 pr-3 text-right">Solo</th>
                <th className="py-2 pr-3 text-right">Avg Size</th>
                <th className="py-2 pr-3 text-right">Max</th>
                <th className="py-2 pr-3 text-right">Ratio</th>
                <th className="py-2 text-right">Pairs / 3s / 4s / 5+</th>
              </tr>
            </thead>
            <tbody>
              {topAttractions.map((a) => (
                <tr key={a.attraction_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-ink-dark">{a.attraction_name}</div>
                    <div className="text-xs text-ink-soft">{a.attraction_type}</div>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(a.total_bands)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(a.inferred_groups)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtNum(a.solo_visitors)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{a.avg_group_size?.toFixed(1) ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{a.max_group_size}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{a.band_to_party_ratio?.toFixed(2) ?? '—'}</td>
                  <td className="py-2 text-right tabular-nums text-xs">
                    {a.distribution.pairs} / {a.distribution.triples} / {a.distribution.quads} / {a.distribution.large}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
